import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage} from 'react-intl';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SignProgressStepper from '../components/SignProgressStepper';

/** Long enough for a slow device to start writing the file before the blob is released. */
const REVOKE_URL_DELAY_MS = 60000;

/** Returns false when sharing is unavailable or declined, so the caller falls back to a download. */
const shareFile = async (blob, fileName) => {
  const file = new File([blob], fileName, {type: blob.type});

  if (!navigator.canShare || !navigator.canShare({files: [file]})) {
    return false;
  }

  try {
    await navigator.share({files: [file]});

    return true;
  } catch (error) {
    // Dismissing the share sheet is a choice, not a failure — do not then force a download.
    return error.name === 'AbortError';
  }
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
    minHeight: '50vh',
  },
  icon: {
    fontSize: theme.typography.pxToRem(100),
    color: theme.palette.success.main,
    marginBottom: 24,
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    maxWidth: 320,
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(16),
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    maxWidth: 380,
    marginTop: 8,
  },
  downloadButton: {
    marginTop: 24,
    borderRadius: 30,
    padding: '12px 32px',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
  },
  hash: {
    fontFamily: 'monospace',
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.disabled,
    wordBreak: 'break-all',
    maxWidth: 380,
    marginTop: 16,
  },
}));

const CompletedScreen = ({signKey, signedAt, documentSha256, documentBase64}) => {
  const {classes} = useStyles();

  // The copy without the evidence appendix is never stored, so this response is the only source.
  // Once the tab is closed the emailed copy takes over.
  const handleDownload = async () => {
    const bytes = Uint8Array.from(atob(documentBase64), (character) => character.charCodeAt(0));
    const blob = new Blob([bytes], {type: 'application/pdf'});
    const fileName = `call-summary-${signKey}.pdf`;

    // In-app browsers ignore the download attribute and block blob navigation, so the native share
    // sheet is the only route from there to the file system.
    if (await shareFile(blob, fileName)) {
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener';
    // Some browsers only dispatch the click for an element that is actually in the document.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking in the same tick can cancel a download that has not started yet.
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_URL_DELAY_MS);
  };

  return (
    <div>
      <SignProgressStepper activeStep={2} />
      <div className={classes.container}>
        <CheckCircleOutlineIcon className={classes.icon} />
        <div className={classes.title}>
          <FormattedMessage id='sign.completed.title' />
        </div>
        <div className={classes.subtitle}>
          <FormattedMessage id='sign.completed.subtitle' />
        </div>
        {signedAt && (
          <div className={classes.subtitle}>
            <FormattedMessage
              id='sign.completed.signedAt'
              values={{date: new Date(signedAt).toLocaleString('he-IL')}}
            />
          </div>
        )}
        {documentBase64 && (
          <Button
            variant='outlined'
            color='primary'
            className={classes.downloadButton}
            onClick={handleDownload}
          >
            <FormattedMessage id='sign.completed.download' />
          </Button>
        )}
        {documentSha256 && <div className={classes.hash} dir='ltr'>SHA-256: {documentSha256}</div>}
      </div>
    </div>
  );
};

CompletedScreen.propTypes = {
  documentBase64: PropTypes.string,
  documentSha256: PropTypes.string,
  signKey: PropTypes.string.isRequired,
  signedAt: PropTypes.string,
};

export default CompletedScreen;
